/**
 * Corporate Sustainability Overview
 * Aggregated sustainability dashboard across all sites.
 * Sections: Emissions Overview, Site Comparison, Aggregation Status, Regulatory Summary
 */

import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { useSite } from '../../context/SiteContext';
import { useAlert } from '../../context/AlertContext';
import {
  BarChart3, Cloud, Zap, Droplets, Trash2, Lock, Unlock,
  CheckCircle, AlertCircle, RefreshCw, Download, Calendar,
  Building2, Globe, TrendingDown, ArrowRight
} from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

const CURRENT_YEAR = new Date().getFullYear();
const YEAR_OPTIONS = Array.from({ length: 5 }, (_, i) => CURRENT_YEAR - i);
const MONTH_OPTIONS = [
  { value: '', label: 'Full Year' },
  { value: 1, label: 'January' }, { value: 2, label: 'February' },
  { value: 3, label: 'March' }, { value: 4, label: 'April' },
  { value: 5, label: 'May' }, { value: 6, label: 'June' },
  { value: 7, label: 'July' }, { value: 8, label: 'August' },
  { value: 9, label: 'September' }, { value: 10, label: 'October' },
  { value: 11, label: 'November' }, { value: 12, label: 'December' }
];

const SCOPE_COLORS = {
  scope_1: { bg: 'bg-red-50', border: 'border-red-200', text: 'text-red-700', bar: 'bg-red-500' },
  scope_2: { bg: 'bg-amber-50', border: 'border-amber-200', text: 'text-amber-700', bar: 'bg-amber-500' },
  scope_3: { bg: 'bg-blue-50', border: 'border-blue-200', text: 'text-blue-700', bar: 'bg-blue-500' },
};

const CorporateView = () => {
  const { activeSiteId, isCorpUser, availableSites } = useSite();
  const { showError, showSuccess } = useAlert();

  const [selectedYear, setSelectedYear] = useState(CURRENT_YEAR);
  const [selectedMonth, setSelectedMonth] = useState('');
  const [loading, setLoading] = useState(true);
  const [locking, setLocking] = useState(false);

  // Data states
  const [emissions, setEmissions] = useState(null);
  const [aggregation, setAggregation] = useState(null);
  const [siteComparison, setSiteComparison] = useState(null);
  const [regulatorySummary, setRegulatorySummary] = useState(null);

  const buildParams = useCallback(() => {
    const params = new URLSearchParams();
    params.append('year', selectedYear);
    if (selectedMonth) params.append('month', selectedMonth);
    if (activeSiteId) params.append('site_id', activeSiteId);
    return params.toString();
  }, [selectedYear, selectedMonth, activeSiteId]);

  const fetchAllData = useCallback(async () => {
    setLoading(true);
    const token = localStorage.getItem('token');
    const headers = { Authorization: `Bearer ${token}` };
    const base = `${API}/api/sustainability/corporate`;
    const qs = buildParams();

    const requests = [
      axios.get(`${base}/emissions?${qs}`, { headers }).catch(() => ({ data: null })),
      axios.get(`${base}/aggregation?${qs}&type=emissions`, { headers }).catch(() => ({ data: null })),
      axios.get(`${base}/site-comparison?${qs}&metric=emissions`, { headers }).catch(() => ({ data: null })),
      axios.get(`${base}/regulatory-summary?${qs}`, { headers }).catch(() => ({ data: null })),
    ];

    try {
      const [emRes, aggRes, compRes, regRes] = await Promise.all(requests);
      setEmissions(emRes.data);
      setAggregation(aggRes.data);
      setSiteComparison(compRes.data);
      setRegulatorySummary(regRes.data);
    } catch (error) {
      console.error('Failed to fetch corporate sustainability data:', error);
      showError('Failed to load corporate sustainability data');
    } finally {
      setLoading(false);
    }
  }, [buildParams, showError]);

  useEffect(() => {
    fetchAllData();
  }, [fetchAllData]);

  const handleLockPeriod = async () => {
    const confirmed = window.confirm(
      `Lock aggregation for ${selectedYear}${selectedMonth ? ` / ${MONTH_OPTIONS.find(m => m.value === selectedMonth)?.label}` : ''}? Locked periods cannot be re-aggregated.`
    );
    if (!confirmed) return;

    setLocking(true);
    try {
      const token = localStorage.getItem('token');
      await axios.post(
        `${API}/api/sustainability/corporate/aggregation/lock`,
        { year: selectedYear, month: selectedMonth || null, type: 'emissions' },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      showSuccess('Aggregation period locked successfully');
      fetchAllData();
    } catch (error) {
      showError('Failed to lock aggregation: ' + (error.response?.data?.detail || error.message));
    } finally {
      setLocking(false);
    }
  };

  const formatNumber = (num, decimals = 1) => {
    if (num == null || isNaN(num)) return '--';
    return Number(num).toLocaleString(undefined, {
      minimumFractionDigits: decimals,
      maximumFractionDigits: decimals
    });
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <RefreshCw className="w-8 h-8 animate-spin text-emerald-500" />
        <span className="ml-3 text-lg text-gray-600">Loading corporate sustainability data...</span>
      </div>
    );
  }

  const totalEmissions = emissions?.total || 0;
  const scope1 = emissions?.scope_1 || 0;
  const scope2 = emissions?.scope_2 || 0;
  const scope3 = emissions?.scope_3 || 0;
  const maxScope = Math.max(scope1, scope2, scope3, 1);

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="bg-gradient-to-r from-emerald-600 to-teal-600 rounded-xl p-6 text-white">
        <div className="flex items-center justify-between">
          <div>
            <h2 className="text-2xl font-bold">Corporate Sustainability Overview</h2>
            <p className="text-emerald-100 mt-1">Aggregated emissions and regulatory status across all sites</p>
          </div>
          <div className="flex items-center gap-3">
            <select
              value={selectedYear}
              onChange={(e) => setSelectedYear(Number(e.target.value))}
              className="px-3 py-2 bg-white/10 border border-white/30 rounded-lg text-white focus:outline-none focus:border-white"
            >
              {YEAR_OPTIONS.map(y => <option key={y} value={y} className="text-gray-900">{y}</option>)}
            </select>
            <select
              value={selectedMonth}
              onChange={(e) => setSelectedMonth(e.target.value ? Number(e.target.value) : '')}
              className="px-3 py-2 bg-white/10 border border-white/30 rounded-lg text-white focus:outline-none focus:border-white"
            >
              {MONTH_OPTIONS.map(m => (
                <option key={m.value} value={m.value} className="text-gray-900">{m.label}</option>
              ))}
            </select>
          </div>
        </div>
      </div>

      {/* Emissions Overview */}
      <div className="bg-white rounded-xl border border-gray-200 p-6">
        <div className="flex items-center justify-between mb-6">
          <h3 className="text-lg font-bold text-gray-900 flex items-center gap-2">
            <Cloud className="w-5 h-5 text-gray-600" />
            Emissions Overview
          </h3>
          <div className="text-right">
            <div className="text-3xl font-bold text-gray-900">{formatNumber(totalEmissions)}</div>
            <div className="text-sm text-gray-500">tCO2e Total</div>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {[
            { key: 'scope_1', label: 'Scope 1 (Direct)', value: scope1, icon: Zap, desc: 'Fuel combustion, process emissions' },
            { key: 'scope_2', label: 'Scope 2 (Energy)', value: scope2, icon: Zap, desc: 'Purchased electricity, steam, heat' },
            { key: 'scope_3', label: 'Scope 3 (Value Chain)', value: scope3, icon: Globe, desc: 'Supply chain, travel, waste' },
          ].map(({ key, label, value, icon: Icon, desc }) => {
            const colors = SCOPE_COLORS[key];
            const pct = totalEmissions > 0 ? ((value / totalEmissions) * 100) : 0;
            return (
              <div key={key} className={`${colors.bg} ${colors.border} border rounded-lg p-4`}>
                <div className="flex items-center justify-between mb-2">
                  <div className="flex items-center gap-2">
                    <Icon className={`w-5 h-5 ${colors.text}`} />
                    <span className={`font-semibold ${colors.text}`}>{label}</span>
                  </div>
                  <span className={`text-xs font-medium ${colors.text}`}>{formatNumber(pct, 0)}%</span>
                </div>
                <div className="text-2xl font-bold text-gray-900 mb-1">{formatNumber(value)}</div>
                <div className="text-xs text-gray-500 mb-3">{desc}</div>
                <div className="w-full bg-gray-200 rounded-full h-2">
                  <div
                    className={`${colors.bar} h-2 rounded-full transition-all`}
                    style={{ width: `${(value / maxScope) * 100}%` }}
                  />
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* Site Comparison */}
      <div className="bg-white rounded-xl border border-gray-200 p-6">
        <h3 className="text-lg font-bold text-gray-900 flex items-center gap-2 mb-4">
          <Building2 className="w-5 h-5 text-gray-600" />
          Site Comparison
        </h3>

        {siteComparison?.sites && siteComparison.sites.length > 0 ? (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-gray-200">
                  <th className="text-left py-3 px-4 font-semibold text-gray-700">Site / Group</th>
                  <th className="text-right py-3 px-4 font-semibold text-gray-700">Scope 1</th>
                  <th className="text-right py-3 px-4 font-semibold text-gray-700">Scope 2</th>
                  <th className="text-right py-3 px-4 font-semibold text-gray-700">Scope 3</th>
                  <th className="text-right py-3 px-4 font-semibold text-gray-700">Total (tCO2e)</th>
                  <th className="text-right py-3 px-4 font-semibold text-gray-700">Share</th>
                </tr>
              </thead>
              <tbody>
                {siteComparison.sites.map((site, idx) => {
                  const siteTotal = (site.scope_1 || 0) + (site.scope_2 || 0) + (site.scope_3 || 0);
                  const share = totalEmissions > 0 ? ((siteTotal / totalEmissions) * 100) : 0;
                  return (
                    <tr key={site.site_id || idx} className="border-b border-gray-100 hover:bg-gray-50">
                      <td className="py-3 px-4">
                        <div className="font-medium text-gray-900">{site.site_name || site.display_label || 'Unknown'}</div>
                        {site.parent_label && (
                          <div className="text-xs text-gray-500">{site.parent_label}</div>
                        )}
                      </td>
                      <td className="text-right py-3 px-4 text-red-600">{formatNumber(site.scope_1)}</td>
                      <td className="text-right py-3 px-4 text-amber-600">{formatNumber(site.scope_2)}</td>
                      <td className="text-right py-3 px-4 text-blue-600">{formatNumber(site.scope_3)}</td>
                      <td className="text-right py-3 px-4 font-semibold text-gray-900">{formatNumber(siteTotal)}</td>
                      <td className="text-right py-3 px-4">
                        <div className="flex items-center justify-end gap-2">
                          <div className="w-16 bg-gray-200 rounded-full h-2">
                            <div className="bg-emerald-500 h-2 rounded-full" style={{ width: `${share}%` }} />
                          </div>
                          <span className="text-gray-600 text-xs w-10 text-right">{formatNumber(share, 0)}%</span>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
              {siteComparison.sites.length > 1 && (
                <tfoot>
                  <tr className="bg-gray-50 font-semibold">
                    <td className="py-3 px-4 text-gray-900">Total</td>
                    <td className="text-right py-3 px-4 text-red-700">
                      {formatNumber(siteComparison.sites.reduce((sum, s) => sum + (s.scope_1 || 0), 0))}
                    </td>
                    <td className="text-right py-3 px-4 text-amber-700">
                      {formatNumber(siteComparison.sites.reduce((sum, s) => sum + (s.scope_2 || 0), 0))}
                    </td>
                    <td className="text-right py-3 px-4 text-blue-700">
                      {formatNumber(siteComparison.sites.reduce((sum, s) => sum + (s.scope_3 || 0), 0))}
                    </td>
                    <td className="text-right py-3 px-4 text-gray-900">{formatNumber(totalEmissions)}</td>
                    <td className="text-right py-3 px-4 text-gray-600">100%</td>
                  </tr>
                </tfoot>
              )}
            </table>
          </div>
        ) : (
          <div className="text-center py-8 text-gray-500">
            <Building2 className="w-10 h-10 mx-auto mb-2 text-gray-300" />
            <p>No site comparison data available for this period</p>
          </div>
        )}
      </div>

      {/* Aggregation Status */}
      <div className="bg-white rounded-xl border border-gray-200 p-6">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-bold text-gray-900 flex items-center gap-2">
            <BarChart3 className="w-5 h-5 text-gray-600" />
            Aggregation Status
          </h3>
          {isCorpUser && aggregation && !aggregation.is_locked && (
            <button
              onClick={handleLockPeriod}
              disabled={locking}
              className="flex items-center gap-2 px-4 py-2 bg-amber-600 text-white rounded-lg hover:bg-amber-700 disabled:opacity-50"
            >
              {locking ? <RefreshCw className="w-4 h-4 animate-spin" /> : <Lock className="w-4 h-4" />}
              Lock Period
            </button>
          )}
        </div>

        {aggregation ? (
          <div className="space-y-4">
            <div className="flex items-center gap-4 p-4 rounded-lg bg-gray-50">
              <div className={`p-2 rounded-lg ${aggregation.is_locked ? 'bg-amber-100' : 'bg-green-100'}`}>
                {aggregation.is_locked ? (
                  <Lock className="w-5 h-5 text-amber-600" />
                ) : (
                  <Unlock className="w-5 h-5 text-green-600" />
                )}
              </div>
              <div>
                <div className="font-semibold text-gray-900">
                  {selectedYear}{selectedMonth ? ` / ${MONTH_OPTIONS.find(m => m.value === selectedMonth)?.label}` : ''}
                </div>
                <div className="text-sm text-gray-600">
                  {aggregation.is_locked ? 'Locked — data is finalized' : 'Unlocked — data can be updated'}
                </div>
              </div>
              <div className="ml-auto text-right">
                <div className="text-sm text-gray-500">Sites contributing</div>
                <div className="font-semibold text-gray-900">{aggregation.contributing_sites || 0}</div>
              </div>
              <div className="text-right">
                <div className="text-sm text-gray-500">Last updated</div>
                <div className="font-semibold text-gray-900 text-sm">
                  {aggregation.last_updated ? new Date(aggregation.last_updated).toLocaleDateString() : '--'}
                </div>
              </div>
            </div>

            {/* Aggregation tree by org node */}
            {aggregation.nodes && aggregation.nodes.length > 0 && (
              <div className="mt-4">
                <h4 className="text-sm font-semibold text-gray-700 mb-2">Aggregation by Org Level</h4>
                <div className="space-y-2">
                  {aggregation.nodes.map((node, idx) => (
                    <div key={node.node_id || idx} className="flex items-center justify-between px-4 py-3 bg-gray-50 rounded-lg">
                      <div className="flex items-center gap-2">
                        {node.depth > 0 && (
                          <span className="text-gray-300" style={{ marginLeft: `${node.depth * 16}px` }}>
                            <ArrowRight className="w-4 h-4" />
                          </span>
                        )}
                        <span className="font-medium text-gray-900">{node.display_label}</span>
                        {node.node_type && (
                          <span className="text-xs px-2 py-0.5 bg-gray-200 text-gray-600 rounded">{node.node_type}</span>
                        )}
                      </div>
                      <div className="flex items-center gap-6 text-sm">
                        <span className="text-red-600">{formatNumber(node.scope_1)} <span className="text-xs text-gray-400">S1</span></span>
                        <span className="text-amber-600">{formatNumber(node.scope_2)} <span className="text-xs text-gray-400">S2</span></span>
                        <span className="text-blue-600">{formatNumber(node.scope_3)} <span className="text-xs text-gray-400">S3</span></span>
                        <span className="font-semibold text-gray-900">{formatNumber(node.total)} tCO2e</span>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        ) : (
          <div className="text-center py-8 text-gray-500">
            <BarChart3 className="w-10 h-10 mx-auto mb-2 text-gray-300" />
            <p>No aggregation data available for this period</p>
          </div>
        )}
      </div>

      {/* Regulatory Summary */}
      <div className="bg-white rounded-xl border border-gray-200 p-6">
        <h3 className="text-lg font-bold text-gray-900 flex items-center gap-2 mb-4">
          <Globe className="w-5 h-5 text-gray-600" />
          Regulatory Framework Status
        </h3>

        {regulatorySummary?.frameworks && regulatorySummary.frameworks.length > 0 ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {regulatorySummary.frameworks.map((fw) => {
              const completion = fw.completion_pct || 0;
              const isComplete = completion >= 100;
              return (
                <div
                  key={fw.framework}
                  className={`border rounded-lg p-4 ${
                    isComplete ? 'border-green-200 bg-green-50' : 'border-gray-200 bg-gray-50'
                  }`}
                >
                  <div className="flex items-center justify-between mb-3">
                    <h4 className="font-semibold text-gray-900">{fw.framework}</h4>
                    {isComplete ? (
                      <CheckCircle className="w-5 h-5 text-green-600" />
                    ) : (
                      <AlertCircle className="w-5 h-5 text-amber-500" />
                    )}
                  </div>
                  <div className="text-2xl font-bold text-gray-900 mb-1">{formatNumber(completion, 0)}%</div>
                  <div className="w-full bg-gray-200 rounded-full h-2 mb-2">
                    <div
                      className={`h-2 rounded-full ${isComplete ? 'bg-green-500' : 'bg-amber-500'}`}
                      style={{ width: `${Math.min(completion, 100)}%` }}
                    />
                  </div>
                  <div className="text-xs text-gray-500">
                    {fw.sites_reporting || 0} of {fw.total_sites || 0} sites reporting
                  </div>
                </div>
              );
            })}
          </div>
        ) : (
          <div className="text-center py-8 text-gray-500">
            <Globe className="w-10 h-10 mx-auto mb-2 text-gray-300" />
            <p>No regulatory summary data available</p>
          </div>
        )}
      </div>
    </div>
  );
};

export default CorporateView;
